import { Component } from '@theme/component';
import { DiscountUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * The Ajax Cart API only reports `applicable: false` for a rejected code. The reason comes back
 * as a cart warning from the Storefront API (tokenless access is enough for cart mutations).
 */
const STOREFRONT_API_URL = '/api/2026-10/graphql.json';
const DISCOUNT_WARNINGS_MUTATION = `mutation CartDiscountWarnings($cartId: ID!, $discountCodes: [String!]!) {
  cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
    warnings { code }
  }
}`;

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Resolves the section ID used by Section Rendering API updates.
   * Falls back to the surrounding cart container when the snippet did not
   * receive data-section-id directly.
   *
   * @returns {string | null}
   */
  #getSectionId() {
    if (typeof this.dataset.sectionId === 'string' && this.dataset.sectionId.length > 0) {
      return this.dataset.sectionId;
    }

    const cartItemsComponent = this.closest('cart-items-component');
    if (cartItemsComponent instanceof HTMLElement && typeof cartItemsComponent.dataset.sectionId === 'string') {
      return cartItemsComponent.dataset.sectionId;
    }

    const sectionElement = this.closest('.shopify-section');
    if (sectionElement?.id?.startsWith('shopify-section-')) {
      return sectionElement.id.replace(/^shopify-section-/, '');
    }

    return null;
  }

  /**
   * Resolves the URL context for section rendering.
   * Drawer markup must be rendered from the current page context, while the
   * cart page should render from the canonical cart URL.
   *
   * @returns {string}
   */
  #getSectionsUrl() {
    const cartItemsComponent = this.closest('cart-items-component');
    if (cartItemsComponent instanceof HTMLElement && cartItemsComponent.dataset.drawer !== undefined) {
      return window.location.pathname + window.location.search;
    }

    return Theme.routes.cart_url;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form =
      event.target instanceof HTMLFormElement
        ? event.target
        : event.target instanceof HTMLElement
        ? event.target.closest('form')
        : null;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    const sectionId = this.#getSectionId();
    if (!(discountCode instanceof HTMLInputElement) || !sectionId) return;

    const discountCodeValue = discountCode.value.trim();

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: sectionId,
          sections_url: this.#getSectionsUrl(),
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Discount update failed with status ${response.status}`);
      }

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        const warningCode = await this.#fetchDiscountWarning(
          [...existingDiscounts, discountCodeValue],
          abortController.signal
        );
        this.#handleDiscountError('discount_code', discountCodeValue, warningCode);
        return;
      }

      const newHtml = data.sections?.[sectionId];
      if (!newHtml) {
        throw new Error(`Missing section HTML for section ${sectionId}`);
      }

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(sectionId, newHtml);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error(error);
        this.#handleDiscountError('discount_code', discountCodeValue);
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Asks the Storefront API why a discount code was not applied. The codes sent are the ones
   * the cart already holds after the Ajax update, so the cart itself does not change.
   *
   * @param {string[]} discountCodes - All discount codes on the cart.
   * @param {AbortSignal} signal - Cancels the lookup when a new request starts.
   * @returns {Promise<string | null>} The CartWarningCode, or null when it cannot be read.
   */
  async #fetchDiscountWarning(discountCodes, signal) {
    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { signal });
      if (!cartResponse.ok) return null;

      const { token } = await cartResponse.json();
      if (typeof token !== 'string' || token.length === 0) return null;

      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          query: DISCOUNT_WARNINGS_MUTATION,
          variables: { cartId: `gid://shopify/Cart/${token}`, discountCodes },
        }),
        signal,
      });
      if (!response.ok) return null;

      const { data } = await response.json();
      /** @type {{ code: string }[]} */
      const warnings = data?.cartDiscountCodesUpdate?.warnings ?? [];
      return warnings.find((warning) => warning.code.startsWith('DISCOUNT_'))?.code ?? null;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return null;
    }
  }

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const sectionId = this.#getSectionId();
    if (!sectionId) return;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: sectionId,
          sections_url: this.#getSectionsUrl(),
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Discount removal failed with status ${response.status}`);
      }

      const data = await response.json();
      const newHtml = data.sections?.[sectionId];
      if (!newHtml) {
        throw new Error(`Missing section HTML for section ${sectionId}`);
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(sectionId, newHtml);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error(error);
        this.#handleDiscountError('discount_code');
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   * @param {string} [code] - The rejected code, named in the message.
   * @param {string | null} [warningCode] - Why the code was rejected, when known.
   */
  #handleDiscountError(type, code = '', warningCode = null) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    if (type === 'discount_code') {
      const message = this.#discountErrorMessage(code, warningCode);
      if (message) target.textContent = message;
    }
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Picks the message for a rejected code from the messages rendered by cart-discount-messages.liquid.
   *
   * @param {string} code - The rejected code; empty when the error is not about a single code.
   * @param {string | null} warningCode - The Storefront API CartWarningCode, when known.
   * @returns {string | null}
   */
  #discountErrorMessage(code, warningCode) {
    const source = this.querySelector('[data-cart-discount-messages]');
    if (!source?.textContent) return null;

    try {
      /** @type {Record<string, string>} */
      const messages = JSON.parse(source.textContent);
      if (!code) return messages.default ?? null;

      const message = (warningCode && messages[warningCode]) || messages.generic;
      return message ? message.replaceAll('__CODE__', code) : null;
    } catch {
      return null;
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
